import type HyBase from "./Base"
import type HyModel from "./Model"
import type HyCollection from "./Collection"
import locationItems from "~/stores/lists/locations" 
import type { Rule } from "./Validation"

// TODO: Make option lists reactive for all SelectField/dropdown fields.

export default class Field {
  // Attribute access
  [key: string]: any

  readonly name!: string
  readonly config!: FieldConfig
  readonly model!: HyBase | HyModel

  static make(name: string, config: FieldConfig, model: HyBase | HyModel) {
    switch (config.type) {
      case 'file':
      case 'image':
        return new BinaryField(name, config, model)
      case 'autocomplete':
      case 'location':
        return new AutocompleteField(name, config, model)
      case 'date':
      case 'time':
      case 'datetime':
        return new DatetimeField(name, config, model)
      case 'select':
      case 'switches':
        return new SelectField(name, config, model)
      // TODO: case 'markdown':
      case 'text':
        return new TextField(name, config, model)
      default:
        return new Field(name, config, model)
    }
  }

  constructor(
    name: string,
    config: FieldConfig,
    model: HyBase | HyModel
  ) {
    // Set name and model
    this.name = name
    this.model = model
    // Initialize defaults
    let defaults: FieldConfig = {}
    // Check for local values
    if (typeof config.type === 'function') {
      config.type = 'local'
      defaults.local = true
    }
    if (defaults.local) {
      // Set local field defaults
      // @ts-expect-error - we know config.type.name will work
      const type = config.type.name
      // Override default for objects and arrays
      switch (type) {
        case 'Object':
          config.type = 'object'
          config.default = () => ({})
          break
        case 'Array':
          config.type = 'array'
          config.default = () => ([])
          break
      }
    } else {
      // Set remote field defaults
      defaults = {
        label: useStartCase(name),
        type: 'string',
        default: '',
        validation: null,
        mutation: null,
        readOnly: false,
        options: {},
        ...defaults
      }
    }
    this.config = useDefaultsDeep(config, defaults)
  }

  get value() {
    return this.model.get(this.name)
  }

  set value(value) {
    this.model.set(this.name, value)
  }
  
  get display() {
    if (isFunction(this.options.display)) {
      return this.options.display(this.model)
    } else {
      return this.value
    }
  }

  get label() {
    return this.config.label
  }

  // ----------
  // Utility ==
  // ----------

  evaluateProperty(property: any) {
    if (typeof property === 'function') {
      // Execute properties that are functions
      return property(this.model)
    } else {
      // Return propeties that are values
      return property
    }
  }

  // --------------------
  // Config Properties ==
  // --------------------

  get type() {
    return this.config.type as FieldTypes
  }

  get options(): FieldOptions {
    return this.config.options || {}
  }

  get validation(): Rule | null {
    return this.config.validation as Rule
  }

  // -------------------
  // HyModel Properties ==
  // -------------------

  get invalid() {
    return !isEmpty(this.model.errors[this.name])
  }

  get errors() {
    return this.model.errors[this.name] || null
  }

  // ----------------------
  // Property Management ==
  // ----------------------

  // TODO: get id () {
  //   const remoteModel = useSnakeCase(this.model.getOption('remoteModel'))
  //   const identifier = this.model.identifier() || 'new'
  //   return `${remoteModel}-${identifier}-${this.name}`
  // }

  reset() {
    this.model.reset(this.name)
  }

  get formDataName() {
    const remoteModel = useSnakeCase(this.model.getOption('remoteModel'))
    return `${remoteModel}[${this.name}]`
  }

  addFormData(formData: FormData) {
    // Don't send back local fields
    if (this.config.local) return
    // Get value from field, or serializer
    const value = isFunction(this.config.options?.serialize) ? this.config.options.serialize(this.model) : this.value
    // Don't send back undefined or null values
    if (value === undefined || value === null) return
    // Is it an array?
    if (isArray(value)) {
      // Is it an empty array?
      if (value.length === 0) {
        // Send an array with an empty string as the value, since formData
        // doesn't support empty arrays.
        formData.append(`${this.formDataName}[]`, '')
      } else {
        // Add elements to form
        value.map((e) => { formData.append(`${this.formDataName}[]`, e) })
      }
    // Is it an object?
    } else if (isObject(value)) {
      // Add each key-value pair to form
      Object.entries(value).forEach(([key, val]) => {
        // String, boolean, number, blob, or file
        if (typeof val === 'string' || typeof val === 'boolean' || typeof val === 'number' || val instanceof Blob || val instanceof File) {
          formData.append(`${this.formDataName}[${key}]`, val as Blob)
        // Array
        } else if (isArray(val)) {
          val.map((e) => { formData.append(`${this.formDataName}[${key}][]`, e) })
        // Object
        } else if (isObject(val)) {
          Object.entries(val).forEach(([k, v]) => {
            formData.append(`${this.formDataName}[${key}][${k}]`, v)
          })
        }
      })
    } else {
      // Add value to form
      formData.append(this.formDataName, value)
    }
  }

  get default() {
    return this.evaluateProperty(this.config.default)
  }

  // ----------
  // Control ==
  // ----------

  control(context: any): Control {
    // Populate control with field properties
    const control = {
      name: this.name,
      type: this.type,
      label: this.config.label,
      placeholder: this.config.placeholder,
      hint: this.config.hint,
      disabled: this.config.readOnly,
      error: this.invalid,
      errorMessages: this.errors,
      counter: false,
      sync: this.options.sync,
      help: this.options.help,
      display: false,
      flag: false,
      max: 0,
      // Styling
      variant: 'solo-filled',
      density: 'compact',
      flat: true
    }
    // Set the counter flag to true if the value is a number
    control.counter = isBoolean(this.options.counter) ? this.options.counter : isNumber(this.options.counter)
    // Set the max flag to the value of the counter flag if it's a number
    control.max = isNumber(this.options.counter) ? this.options.counter : 0
    // Context overrides
    // TODO: Use defaultsDeep() for this
    if (context) {
      control.label = (context.label === false) ? undefined : context.label || control.label
      control.hint = (context.hint === false) ? undefined : context.hint || control.hint
      control.placeholder = (context.placeholder === false) ? undefined : context.placeholder || control.placeholder
      control.disabled = (context.disabled ?? control.disabled) ?? false
      control.display = (context.display ?? control.display) ?? false
      control.flag = context.flag !== undefined ? context.flag : false
      // control.autofocus = context.autofocus || control.autofocus
    }
    return control as Control
  }
}

class AutocompleteField extends Field {
  readonly remote!: Boolean

  constructor(name: string, config: FieldConfig, model: HyBase | HyModel) {
    // Initialize location inputs
    if (config.type === 'location') {
      config.options = {
        itemTitle: 'place_name',
        items: locationItems,
        display: (model: HyModel) => model.get('location_name'),
        autocomplete: (value: any) => {
          // Set field to Mapbox JSON
          this.model.set(this.name, JSON.stringify(value))
          // Set location name
          this.model.set('location_name', value.place_name)
        },
        ...config.options
      }
    }
    // Initialize field
    super(name, config, model)
    // Initialize remote
    if (isFunction(this.options.items)) {
      this.remote = true
    } else {
      this.remote = false
    }
  }

  async items(query: string | undefined) {
    if (this.remote) {
      return await this.fetchItems(unref(query))
    } else {
      return this.options.items
    }
  }

  async fetchItems(query: string | undefined) {
    // No query, nothing to do
    if (!query) return []
    // We need more than one character
    if (query.length < 2) return []
    // Ignore existing display value
    if (query === this.display) return []
    // Set items from control query
    return await this.options.items(query)
  }

  override control(context: any) {
    const control = super.control(context)
    return {
      ...control,
      multiple: this.options.multiple || false,
      itemTitle: this.options.itemTitle,
      itemValue: this.options.itemValue
    }
  }
}

class BinaryField extends Field {

  // TODO: Manage multiple uploads
  override addFormData(formData: FormData) {
    formData.append(this.formDataName, this.value)
  }

  override get display() {
    if (this.value instanceof File) {
      // Is it an image?
      if (this.value.type.startsWith('image')) {
        return URL.createObjectURL(this.value);
      } else {
        // Not an image
        return false
      }
    } else {
      return this.value?.url
    }
  }

  override control(context: any): Control {
    const control = super.control(context)
    return {
      ...control,
      multiple: this.options.multiple || false,
      type: 'file'
    }
  }
}

class DatetimeField extends Field {

  override control(context: any): Control {
    // Get common attributes
    const control = super.control(context)
    // Set type
    let type = 'datetime-local'
    if (this.type !== 'datetime') { type = this.type as string }
    // Return merged attributes
    return {
      ...control,
      type
    }
  }
}

class SelectField extends Field {

  // Display the item label instead of the value
  override get display() {
    if (isFunction(this.options.display)) {
      return this.options.display(this.model)
    } else {
      if (isArray(this.options.items)) {
        return this.options.items.find((item: any) => item.value === this.value)?.title || this.value
      } else {
        return this.value
      }
    }
  }

  // Handle various item types reactively
  items(query: string | undefined) {
    
    // Arrow Functions () =>
    if (isFunction(this.options.items)) {
      return this.options.items(query)
    
    // Static Arrays
    } else {
      return this.options.items
    }
  }
}

class TextField extends Field {

  override control(context: any): Control {
    const control = super.control(context)
    return {
      ...control,
      rows: context?.rows || this.options.rows || 3,
      autoGrow: context?.autoGrow || this.type === 'autogrow'
    }
  }
}

export type FieldTypes =
  // Local types
  'local' | StringConstructor | NumberConstructor | ArrayConstructor | ObjectConstructor |
  // Special
  'collection' | typeof HyCollection | 'model' | typeof HyModel | 'virtual' |
  // Inputs
  'string' | 'text' | 'select' | 'file' | 'image' |
  'number' | 'email' | 'switch' | 'switches' | 'tags' |
  'url' | 'autogrow' | 'location' | 'autocomplete' |
  'date' | 'time' | 'datetime' |
  'object' | 'array'

export interface FieldConfig {
  type?: FieldTypes
  label?: string | boolean
  default?: string | number | boolean | null | Function
  placeholder?: string | boolean
  hint?: string | boolean
  validation?: Function | null
  mutation?: string | number | null
  readOnly?: boolean
  local?: boolean
  collection?: boolean
  disabled?: boolean
  // Options - settings that are specific to the field type
  options?: FieldOptions
}

export interface FieldOptions {
  // General
  display?: Function
  serialize?: Function
  sync?: ("Affinity" | "Wordpress")[]
  help?: string
  // Autocomplete
  autocomplete?: Function
  itemTitle?: string
  itemValue?: string
  // Select / Autocomplete
  items?: any
  // Files
  multiple?: boolean
  // Text
  rows?: number | string
  counter?: boolean | number
  autoGrow?: boolean
}
export interface Control {
  name: string
  type: string
  label: string | undefined
  placeholder: string | undefined
  hint: string | undefined
  sync: ("Affinity" | "Wordpress")[] | undefined
  help: string | undefined
  disabled: boolean | undefined
  display: boolean
  flag: boolean
  error: boolean
  errorMessages: string | string[] | null
  // Select
  // items can be either an array of strings, or an array of objects with a 'value' and 'title' property
  items?: string[] | Record<string, any>[] | undefined
  // Text
  rows?: number | string
  autoGrow?: boolean
  counter?: true | undefined
  max?: number
  // Files / Images
  multiple?: boolean
}

export type Manifest = Record<string, FieldConfig>
